import { PaginationBar } from '@systemeio/components';
import React, { useEffect, useState } from 'react';
import { BaseTableProps } from 'shared/components/table/components/base-table';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { OrderEnum } from 'shared/types/pagination-interface';
export type PageTemplateLimitType = 9 | 15 | 30;
const limitArray: PageTemplateLimitType[] = [9, 15, 30];
interface PageTemplatePaginationProps<T> extends Pick<BaseTableProps<T>, 'data' | 'paginator'> {
  limiter: {
    limit: PageTemplateLimitType;
    setLimit: React.Dispatch<React.SetStateAction<PageTemplateLimitType>>;
  };
}
function PageTemplatePagination<T extends {
  id: number | string;
}>({
  data,
  limiter,
  paginator
}: PageTemplatePaginationProps<T>) {
  const {
    t
  } = useLocoTranslation();
  if (!paginator || !limiter) {
    return null;
  }
  const {
    pagination,
    setPagination
  } = paginator;
  const {
    limit,
    setLimit
  } = limiter;
  const [isPaginationShown, setIsPaginationShown] = useState(true);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  useEffect(() => {
    if (!isFirst && data?.items.length === 0) {
      setPagination({
        order: OrderEnum.next
      });
    }
  }, [data?.items.length, isFirst, setPagination]);
  useEffect(() => {
    if (data) {
      setIsFirst(true);
      setIsLast(false);
      switch (pagination.order) {
        case 'next':
          {
            if (pagination.startFromId) {
              setIsFirst(false);
            }
            if (!data.hasMore) {
              setIsLast(true);
            }
            break;
          }
        case 'prev':
          {
            if (data.hasMore) {
              setIsFirst(false);
            }
          }
      }
    }
  }, [data, pagination.order, pagination.startFromId]);
  useEffect(() => {
    if (data) {
      setIsPaginationShown(true);
      if (!isFirst || limiter.limit !== 9) return;
      if (pagination.order === 'next' && !data.hasMore) {
        setIsPaginationShown(false);
      }
      if (data.items.length === 0) {
        setIsPaginationShown(false);
      }
    } else {
      setIsPaginationShown(false);
    }
  }, [data, isFirst, limiter.limit, pagination.order, pagination.startFromId]);
  return isPaginationShown ? <PaginationBar hasNextPage={!isLast} hasPreviousPage={!isFirst} limit={limit} onLimitChange={nextLimit => {
    setLimit(nextLimit as PageTemplateLimitType);
    setPagination({
      startFromId: undefined,
      order: OrderEnum.next
    });
  }} onPreviousPage={() => {
    const firstItem = data?.items[0];
    if (!firstItem) return;
    setPagination({
      startFromId: firstItem.id,
      order: OrderEnum.prev
    });
  }} onNextPage={() => {
    const lastItem = data?.items[data.items.length - 1];
    if (!lastItem) return;
    setPagination({
      startFromId: lastItem.id,
      order: OrderEnum.next
    });
  }} selectProps={{
    'aria-label': t('global.limit'),
    options: limitArray
  }} prevButtonProps={{
    'aria-label': t('global.previous')
  }} nextButtonProps={{
    'aria-label': t('global.next')
  }} /> : null;
}
export default PageTemplatePagination;